import maplibregl from 'maplibre-gl'
import { MAP_STYLE, GLYPHS, LABEL_FONT, INITIAL_VIEW, KIND_COLOR } from './config.js'
import { BASEMAPS, LANDSD_LOGO } from './basemaps.js'
import { pick } from './i18n.js'

export let map = null

/* The historical coastline lives on a SECOND, transparent map stacked over the
   first. That sounds excessive until you try to build swipe-compare without it:
   MapLibre can set a layer's opacity but cannot clip a layer to part of the
   viewport, so the only way to show "1900 on the left, today on the right" is a
   separate canvas with a CSS clip-path. Keeping the coastline there permanently
   — rather than moving it between maps when swipe is toggled — means there is
   one code path instead of two, and toggling swipe is just a clip change. */
export let overlayMap = null

const empty = () => ({ type: 'FeatureCollection', features: [] })

export function initMap(onStoryClick) {
  map = new maplibregl.Map({
    container: 'map',
    style: MAP_STYLE,
    center: INITIAL_VIEW.center,
    zoom: INITIAL_VIEW.zoom,
    maxBounds: INITIAL_VIEW.maxBounds,
    attributionControl: false,
    hash: 'at',
    // Vector basemap glyph CDNs ship no Han glyphs — the CJK ranges come back
    // as empty fontstacks — so MapLibre renders Chinese labels with a local
    // system font. Left to its default of 'sans-serif', a machine without a
    // Traditional-leaning face installed will draw 繁體 place names in
    // Simplified glyph forms. On a Cantonese oral-history map that is not a
    // cosmetic issue, so the stack is pinned to Traditional faces first.
    localIdeographFontFamily:
      "'PingFang HK', 'Noto Sans HK', 'Microsoft JhengHei', 'Heiti TC', sans-serif",
  })

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')
  map.addControl(new maplibregl.ScaleControl({ maxWidth: 110, unit: 'metric' }), 'bottom-right')
  applyAttribution()

  buildOverlayMap()

  // MapLibre reports style, tile and worker failures through an 'error' event
  // rather than by throwing. With nothing listening, a basemap that never loads
  // leaves this promise pending forever and boot() stops silently half way —
  // rail rendered, no map, no message, nothing in the console. Surface it.
  map.on('error', (e) => {
    console.error('[tideline] map error:', e?.error?.message || e?.error || e)
  })

  return new Promise((resolve, reject) => {
    let settled = false

    map.on('load', () => {
      // Anything thrown in here used to vanish: MapLibre swallows exceptions
      // raised inside its event handlers, so a bad layer definition meant
      // resolve() was never reached and boot() waited on this promise forever.
      try {
        addStoryLayers(onStoryClick)
        settled = true
        resolve(map)
      } catch (err) {
        settled = true
        reject(err)
      }
    })

    // Watchdog for the case where 'load' simply never arrives — a dead network
    // or an unreachable style. Guard on whether this promise settled, not on
    // map.loaded(): the map can report itself loaded while the handler above
    // has failed, which is exactly the state that used to hang silently.
    setTimeout(() => {
      if (!settled) {
        reject(new Error('Base map did not finish loading — check the network and the style URL.'))
      }
    }, 20000)
  })
}

function buildOverlayMap() {
  const host = document.createElement('div')
  host.id = 'coastline-overlay'
  Object.assign(host.style, {
    position: 'absolute',
    inset: '0',
    zIndex: '2',
    pointerEvents: 'none',   // the overlay is decoration; clicks belong to the base map
  })
  document.getElementById('map').appendChild(host)

  overlayMap = new maplibregl.Map({
    container: host,
    // No background layer, so the canvas is transparent and the base map shows
    // through wherever the coastline sources have nothing to draw. `glyphs` is
    // omitted entirely rather than set to undefined — the style validator
    // rejects the key being present with no string value.
    style: { version: 8, sources: {}, layers: [] },
    center: INITIAL_VIEW.center,
    zoom: INITIAL_VIEW.zoom,
    attributionControl: false,
    interactive: false,
  })

  // Keep the two cameras locked together. jumpTo (not easeTo) so the overlay
  // never lags behind by an animation frame during a drag.
  const sync = () => {
    overlayMap.jumpTo({
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    })
  }
  map.on('move', sync)
  map.on('moveend', sync)
}

/* ── Story layers ────────────────────────────────────────────────────────── */

function addStoryLayers(onStoryClick) {
  map.addSource('stories', { type: 'geojson', data: empty() })
  map.addSource('arcs', { type: 'geojson', data: empty() })

  // Journey arcs sit underneath the points so a pin is never hidden by a line.
  map.addLayer({
    id: 'arc-lines',
    type: 'line',
    source: 'arcs',
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': KIND_COLOR.relocation,
      'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1.1, 15, 2.6],
      'line-opacity': 0.75,
      // Dashes read as movement rather than as a fixed boundary — these are
      // journeys, not borders.
      'line-dasharray': [2, 1.6],
    },
  })

  map.addLayer({
    id: 'arc-heads',
    type: 'circle',
    source: 'arcs',
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 15, 5],
      'circle-color': '#f7f4ee',
      'circle-stroke-color': KIND_COLOR.relocation,
      'circle-stroke-width': 1.6,
    },
  })

  map.addLayer({
    id: 'story-points',
    type: 'circle',
    source: 'stories',
    paint: {
      'circle-radius': [
        'interpolate', ['linear'], ['zoom'],
        9,  ['case', ['boolean', ['feature-state', 'active'], false], 8, 5],
        15, ['case', ['boolean', ['feature-state', 'active'], false], 15, 10],
      ],
      'circle-color': [
        'match', ['get', 'kind'],
        'story',      KIND_COLOR.story,
        'relocation', KIND_COLOR.relocation,
        'ecology',    KIND_COLOR.ecology,
        'place',      KIND_COLOR.place,
        '#888',
      ],
      'circle-opacity': 0.9,
      'circle-stroke-color': '#f7f4ee',
      'circle-stroke-width': [
        'case', ['boolean', ['feature-state', 'active'], false], 3, 1.5,
      ],
      // A withheld location is drawn softer, so the map itself communicates
      // that the point is deliberately imprecise.
      'circle-blur': ['case', ['==', ['get', 'withheld'], true], 0.55, 0],
    },
  })

  map.addLayer({
    id: 'story-labels',
    type: 'symbol',
    source: 'stories',
    minzoom: 13,
    layout: {
      'text-field': ['get', 'label'],
      // Must be set explicitly — see LABEL_FONT in config.js for why the
      // default is a trap on this glyph endpoint.
      'text-font': LABEL_FONT,
      'text-size': 11,
      'text-offset': [0, 1.3],
      'text-anchor': 'top',
      'text-max-width': 12,
      'text-allow-overlap': false,
      'text-optional': true,
    },
    paint: {
      'text-color': '#0f1b21',
      'text-halo-color': '#f7f4ee',
      'text-halo-width': 1.6,
    },
  })

  map.on('click', 'story-points', (e) => {
    if (e.features?.length) onStoryClick(e.features[0].properties.id)
  })
  map.on('mouseenter', 'story-points', () => { map.getCanvas().style.cursor = 'pointer' })
  map.on('mouseleave', 'story-points', () => { map.getCanvas().style.cursor = '' })
}

let activeId = null

export function setActive(id) {
  if (activeId != null) {
    map.setFeatureState({ source: 'stories', id: activeId }, { active: false })
  }
  activeId = id
  if (id != null) {
    map.setFeatureState({ source: 'stories', id }, { active: true })
  }
}

/* MapLibre feature-state needs a numeric or string feature id it can index, and
   our ids are UUIDs. Map each UUID onto a stable integer for the lifetime of
   the page. */
const idToNum = new Map()
const numToId = new Map()
function numericId(uuid) {
  if (!idToNum.has(uuid)) {
    const n = idToNum.size + 1
    idToNum.set(uuid, n)
    numToId.set(n, uuid)
  }
  return idToNum.get(uuid)
}
export const resolveId = (n) => numToId.get(n) ?? n

/**
 * Quadratic bezier between two points, bulged perpendicular to the line.
 *
 * Straight lines between origin and destination overlap into an unreadable
 * bundle as soon as several families move from the same shelter to the same
 * estate — which is exactly the pattern this project is trying to show. Curving
 * each one, with the bulge scaled to its own length, separates them.
 */
function arc(from, to, steps = 48) {
  const [x1, y1] = from
  const [x2, y2] = to
  const dx = x2 - x1
  const dy = y2 - y1
  const len = Math.hypot(dx, dy)
  if (len === 0) return [from, to]

  // 18% of the span, perpendicular. Enough to separate neighbours, not so much
  // that a short hop across a typhoon shelter loops absurdly.
  const bulge = len * 0.18
  const cx = (x1 + x2) / 2 - (dy / len) * bulge
  const cy = (y1 + y2) / 2 + (dx / len) * bulge

  const pts = []
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const u = 1 - t
    pts.push([
      u * u * x1 + 2 * u * t * cx + t * t * x2,
      u * u * y1 + 2 * u * t * cy + t * t * y2,
    ])
  }
  return pts
}

/** Push a filtered story list onto the map. */
export function renderStories(stories) {
  const points = {
    type: 'FeatureCollection',
    features: stories.map((s) => ({
      type: 'Feature',
      id: numericId(s.id),
      geometry: { type: 'Point', coordinates: [s.origin_lng, s.origin_lat] },
      properties: {
        id: s.id,
        kind: s.kind,
        label: pick(s, 'title'),
        withheld: s.precision_level === 'withheld',
      },
    })),
  }

  const journeys = stories.filter((s) => s.kind === 'relocation' && s.dest_lng != null)
  const arcs = {
    type: 'FeatureCollection',
    features: [
      ...journeys.map((s) => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: arc([s.origin_lng, s.origin_lat], [s.dest_lng, s.dest_lat]),
        },
        properties: { id: s.id },
      })),
      ...journeys.map((s) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.dest_lng, s.dest_lat] },
        properties: { id: s.id },
      })),
    ],
  }

  map.getSource('stories')?.setData(points)
  map.getSource('arcs')?.setData(arcs)
}

export function setKindVisible(kind, visible) {
  // Record the intent first, then apply. If the style is mid-swap we still
  // remember what the visitor asked for, and setBasemap replays it afterwards.
  visibleKinds[kind] = visible
  if (!map?.getLayer('story-points')) return
  const shown = Object.entries(visibleKinds).filter(([, v]) => v).map(([k]) => k)
  const filter = ['in', ['get', 'kind'], ['literal', shown]]
  map.setFilter('story-points', filter)
  map.setFilter('story-labels', filter)

  const arcsOn = visibleKinds.relocation
  for (const id of ['arc-lines', 'arc-heads']) {
    map.setLayoutProperty(id, 'visibility', arcsOn ? 'visible' : 'none')
  }
}

const visibleKinds = { story: true, relocation: true, ecology: true, place: true }

/* ── Base map switching ──────────────────────────────────────────────────── */

/* Switching the base map means calling setStyle, and setStyle throws away every
   source and layer we added — stories, arcs, the lot. So the sequence is:
   build the new style, swap it, then rebuild our layers on top and let the
   caller push the current filtered rows back in. `onReady` is that callback.

   The alternative — keeping one style and toggling the visibility of every
   layer in the Liberty style — means enumerating a hundred-odd vendor layer ids
   and re-doing it whenever the upstream style changes. This is the stable one. */

let currentBasemap = 'street'
export const basemap = () => currentBasemap

/* Attribution has to follow the basemap, not sit fixed in the corner. Leaving
   the OpenStreetMap credit up while the Lands Department's tiles are on screen
   credits the wrong people for the wrong map, and drops the credit the licence
   we are actually relying on requires.

   No customAttribution: every source carries its own. OpenFreeMap's style JSON
   already supplies "OpenFreeMap © OpenMapTiles Data from OpenStreetMap", which
   is exactly the credit its terms ask for, and the Lands Department rasters
   carry theirs in basemaps.js. Hardcoding a string here on top of that produced
   a duplicated credit — and, worse, kept crediting OpenStreetMap for tiles it
   had nothing to do with. The control is recreated because AttributionControl
   recomputes its contents from the style's sources when it mounts. */
let attribCtrl = null

function applyAttribution() {
  if (attribCtrl) map.removeControl(attribCtrl)
  attribCtrl = new maplibregl.AttributionControl({ compact: true })
  map.addControl(attribCtrl, 'bottom-right')
}

function rasterStyle(bm, lang) {
  const sources = {}
  const layers = []
  for (const l of bm.layers) {
    const src = { ...l.source }
    if (l.localised) src.tiles = src.tiles.map((u) => u.replace('{lang}', lang))
    sources[l.id] = src
    layers.push({ id: l.id, type: 'raster', source: l.id })
  }
  // glyphs is required: our story-labels symbol layer is rebuilt onto this
  // style, and a symbol layer with no glyph source renders nothing at all.
  return { version: 8, glyphs: GLYPHS, sources, layers }
}

export function setBasemap(id, lang, onStoryClick, onReady) {
  const bm = BASEMAPS[id]
  if (!bm || id === currentBasemap) return
  currentBasemap = id

  map.setStyle(bm.kind === 'style' ? MAP_STYLE : rasterStyle(bm, lang))

  // `once('styledata')` rather than 'load' — the map has already loaded once,
  // so 'load' will never fire again.
  map.once('styledata', () => {
    addStoryLayers(onStoryClick)
    // setStyle discarded the filters along with the layers.
    for (const [kind, on] of Object.entries(visibleKinds)) setKindVisible(kind, on)
    activeId = null
    onReady?.()
  })

  applyAttribution()
  updateLogo()
}

/** Re-fetch the label tiles in the new language. Only the Lands Department
    basemaps have per-language labels; the vector style handles its own. */
export function setBasemapLang(lang) {
  const bm = BASEMAPS[currentBasemap]
  if (!bm || bm.kind === 'style') return
  for (const l of bm.layers) {
    if (!l.localised) continue
    const src = map.getSource(l.id)
    src?.setTiles?.(l.source.tiles.map((u) => u.replace('{lang}', lang)))
  }
}

/* The Lands Department licence asks for the logo on the map face, not merely a
   text credit in the attribution control. Honouring that is the condition of
   using the tiles, so it is done in code rather than left to the deployer. */
function updateLogo() {
  const needed = BASEMAPS[currentBasemap]?.logo === 'landsd'
  let el = document.getElementById('landsd-logo')
  if (!needed) { el?.remove(); return }
  if (el) return
  el = document.createElement('img')
  el.id = 'landsd-logo'
  el.src = LANDSD_LOGO
  el.alt = 'Lands Department 地政總署'
  document.getElementById('map').appendChild(el)
}

/* Padding keeps the arc clear of the rail (left) and the detail panel (right),
   but MapLibre refuses the whole fitBounds — silently, with only a console
   warning — if the padding leaves no canvas behind. 340 + 440 needs an 800px-wide
   map before it will draw anything at all, so on a phone every relocation click
   did nothing. Scale the ask down to what the canvas can actually give. */
function panelPadding() {
  const { width, height } = map.getCanvas().getBoundingClientRect()
  const fit = (a, b, extent) => {
    const room = extent * 0.7          // always leave 30% of the axis for the map
    const scale = Math.min(1, room / (a + b))
    return [Math.round(a * scale), Math.round(b * scale)]
  }
  const [left, right] = fit(340, 440, width)
  const [top, bottom] = fit(120, 200, height)
  return { top, bottom, left, right }
}

export function flyToStory(s) {
  const hasDest = s.kind === 'relocation' && s.dest_lng != null
  if (hasDest) {
    const b = new maplibregl.LngLatBounds(
      [s.origin_lng, s.origin_lat], [s.origin_lng, s.origin_lat]
    ).extend([s.dest_lng, s.dest_lat])
    map.fitBounds(b, { padding: panelPadding(), maxZoom: 15 })
  } else {
    map.easeTo({ center: [s.origin_lng, s.origin_lat], zoom: Math.max(map.getZoom(), 14) })
  }
}
